import { ChatUser, ConversationDetails } from "@virtualprogaming/js-public-sdk";
import classNames from "classnames";
import { find, isEmpty, map, noop, partial } from "lodash";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { useIsPremium } from "~/hooks";
import {
  useChatCreateConversation,
  useChatLimit,
  useChatUsers,
  useSearchChatUsers,
} from "~/queries";
import { Boundary } from "~/ui/components/boundary";
import { Button } from "~/ui/components/buttons";
import { Icon, LoadingIcon } from "~/ui/components/icons";
import { TextInput } from "~/ui/components/inputs";
import {
  SelectedWindows,
  useChatAuthAccessToken,
  useChatContext,
} from "~/ui/context";
import { useScreenMd } from "~/utils";

import { ChatUsersRenderer } from "../chatUsersRenderer/ChatUsersRenderer";

const limit = 15;

interface CreateChatProps {
  outsideSelectedUsers?: ChatUser[];
  setOutsideSelectedUsers?: React.Dispatch<React.SetStateAction<ChatUser[]>>;
  shouldSubmit?: boolean;
  selectedUsersClassname?: string;
}

export const CreateChat: FC<CreateChatProps> = ({
  outsideSelectedUsers,
  setOutsideSelectedUsers = noop,
  shouldSubmit = true,
  selectedUsersClassname,
}) => {
  const isDesktop = useScreenMd();
  const isPremium = useIsPremium();
  const accessToken = useChatAuthAccessToken();
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<ChatUser[]>([]);
  const {
    setSelectedChat,
    setSelectedWindow,
    setIsChatOpen,
  } = useChatContext();
  const ref = useRef<HTMLDivElement>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const { push } = useHistory();

  const { data: chatLimit } = useChatLimit();

  const hasReachedLimit = useMemo(
    () => !isPremium && chatLimit && chatLimit > 5,
    [chatLimit, isPremium]
  );

  const [conversationToCreate, setConversationToCreate] = useState<
    number | null
  >(null);
  const { data: chatUsers, isLoading } = useChatUsers(limit);
  const {
    data: chatSearchUsers,
    isLoading: isChatSearchUsersLoading,
  } = useSearchChatUsers(searchTerm, limit);
  const {
    mutateAsync: createConversation,
    isLoading: isCreateConversationLoading,
  } = useChatCreateConversation();

  useEffect(() => {
    if (
      isEmpty(selectedUsers) &&
      !shouldSubmit &&
      outsideSelectedUsers &&
      !isEmpty(outsideSelectedUsers)
    ) {
      setSelectedUsers(outsideSelectedUsers);
    }
  }, [shouldSubmit, outsideSelectedUsers, selectedUsers]);

  useEffect(() => {
    if (selectedUsers?.length === 1 && !isDisabled && !isPremium) {
      setIsDisabled(true);
      setIsInputFocused(false);
      setSearchTerm("");
    }
  }, [selectedUsers, isPremium, isDisabled]);

  const handleCreateConversation = () => {
    if (isEmpty(selectedUsers)) return;
    if (!shouldSubmit || (!shouldSubmit && isEmpty(outsideSelectedUsers)))
      return;
    const data = map(selectedUsers, ({ id }) => id ?? 0);
    createConversation([accessToken, data]).then(
      (res: { status: number; data: ConversationDetails }) => {
        if (res.status === 200 && res?.data?.id) {
          setSelectedChat(res.data.id);
        }
        setSelectedWindow(SelectedWindows.Conversation);
        setConversationToCreate(null);
        setOutsideSelectedUsers([]);
        setSelectedUsers([]);
      }
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isInputFocused) {
      setIsInputFocused(true);
    }
    setSearch(e.target.value);
  };

  const addUser = (uid: string | number | undefined) => {
    if (!uid) return;
    let user: ChatUser | undefined = undefined;
    if (searchTerm && chatSearchUsers?.data) {
      user = find(chatSearchUsers?.data, (user: ChatUser) => user?.uid === uid);
    } else {
      if (chatUsers?.data) {
        user = find(chatUsers?.data, (user: ChatUser) => user?.uid === uid);
      }
    }
    setSelectedUsers((prevSelectedUsers: ChatUser[]) => {
      if (user) {
        const userAlreadyThere = prevSelectedUsers.find(
          (user) => user.uid === uid
        );
        if (userAlreadyThere) {
          setOutsideSelectedUsers(
            prevSelectedUsers.filter((user) => user.uid !== uid)
          );

          return prevSelectedUsers.filter((user) => user.uid !== uid);
        } else {
          setOutsideSelectedUsers([...prevSelectedUsers, user]);
          return [...prevSelectedUsers, user];
        }
      }

      return prevSelectedUsers;
    });
  };

  const removeUser = (uid: string | number | undefined) => {
    if (!uid) return;
    setSelectedUsers((prevSelectedUsers: ChatUser[]) => {
      let users = [...prevSelectedUsers];
      const user = users.find((user) => user.uid === uid);
      if (user) {
        users = prevSelectedUsers.filter((user) => user.uid !== uid);
      }
      setOutsideSelectedUsers(users);
      if (!isPremium && users.length === 0) {
        setIsDisabled(false);
      }
      return users;
    });
  };

  useEffect(() => {
    const handleOnClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsInputFocused(false);
      }
    };
    document.addEventListener("mousedown", handleOnClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleOnClickOutside);
    };
  }, [ref, setIsInputFocused]);

  useEffect(() => {
    setIsInputFocused(true);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(search);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const handlePremiumClick = () => {
    push("/shop");
    setIsChatOpen(false);
  };

  return (
    <div className="h-95 flex flex-col justify-between relative">
      <Boundary centerStatus isLoading={isLoading}>
        {hasReachedLimit ? (
          <div
            className="as-strong text-center text-muted pointer flex flex-col justify-center items-center bg-light pd-sm borderR-05 w-90 mg-0-auto gap-md mg-t-xl"
            onClick={handlePremiumClick}
          >
            <FormattedMessage
              defaultMessage="You have reached the limit for chat creation for non-premium users"
              id="PXaQTS"
            />
            <small className="font-variant-md-caps">
              <FormattedMessage
                defaultMessage="Click here to get premium and create unlimited conversations"
                id="+a/CGj"
              />
            </small>
          </div>
        ) : (
          <div
            className={classNames(
              "flex flex-col gap-md mg-0-auto h-95",
              isDesktop ? "w-80" : "w-90"
            )}
          >
            <div className="flex flex-col h-10">
              <div
                className={classNames("h-full", isDesktop && "w-80 mg-0-auto")}
              >
                <TextInput
                  onFocus={partial(setIsInputFocused, true)}
                  onClick={partial(setIsInputFocused, true)}
                  value={search}
                  onChange={handleSearchChange}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Search users",
                    id: "JjGgXI",
                  })}
                  boxSize="lg"
                  wrapperClassname="mg-sm"
                  autoFocus={isInputFocused}
                  disabled={isDisabled}
                />
              </div>
              {isInputFocused && (
                <div
                  className={classNames(
                    "absolute bg-light borderR-02 z-index-400 top-58px",
                    isDesktop ? "w-60 left-20p" : "w-87 right-6p"
                  )}
                  ref={ref}
                >
                  <div className="flex justify-between items-center pd-sm pd-r-md pd-l-md">
                    <span className="font-variant-md-caps">
                      <FormattedMessage defaultMessage="Users" id="YDMrKK" />
                    </span>
                    <Icon
                      onClick={partial(setIsInputFocused, false)}
                      icon={["fal", "times"]}
                      size="lg"
                      className="pointer"
                    />
                  </div>
                  {searchTerm === "" ? (
                    <ChatUsersRenderer
                      wrapperClassname="w-90 h-max-300px w-100 mg-0-auto pd-xs pd-b-xxl"
                      users={chatUsers?.data ?? []}
                      onClick={addUser}
                      useDisplayName={false}
                      isLoading={isLoading}
                      isActionLoading={isCreateConversationLoading}
                      actionItemId={conversationToCreate}
                      icon={
                        <Icon
                          icon={["far", "user-plus"]}
                          size="lg"
                          className="pd-r-sm pointer"
                        />
                      }
                      secondIconCondition={{
                        icon: (
                          <Icon
                            icon={["far", "user-minus"]}
                            size="lg"
                            className="pd-r-sm pointer"
                          />
                        ),
                        arrayToFind:
                          !shouldSubmit && outsideSelectedUsers
                            ? outsideSelectedUsers
                            : selectedUsers ?? [],
                      }}
                    />
                  ) : (
                    <div>
                      {isChatSearchUsersLoading ? (
                        <LoadingIcon className="mg-0-auto flex justify-center pd-none" />
                      ) : (
                        <ChatUsersRenderer
                          wrapperClassname="w-90 h-max-300px w-100 mg-0-auto pd-xs pd-b-xl"
                          users={chatSearchUsers?.data ?? []}
                          onClick={addUser}
                          useDisplayName
                          isLoading={isChatSearchUsersLoading}
                          isActionLoading={isCreateConversationLoading}
                          actionItemId={conversationToCreate}
                          icon={
                            <Icon
                              icon={["far", "user-plus"]}
                              size="lg"
                              className="pd-r-sm pointer"
                            />
                          }
                          secondIconCondition={{
                            icon: (
                              <Icon
                                icon={["far", "user-minus"]}
                                size="lg"
                                className="pd-r-sm pointer"
                              />
                            ),
                            arrayToFind:
                              !shouldSubmit && outsideSelectedUsers
                                ? outsideSelectedUsers
                                : selectedUsers ?? [],
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {(!shouldSubmit && isEmpty(outsideSelectedUsers)) ||
            isEmpty(selectedUsers) ? (
              <div>
                <h6 className="text-center font-variant-small-caps mg-t-2em">
                  <FormattedMessage
                    defaultMessage="Search and select users to create a conversation."
                    id="e53Ln+"
                  />
                </h6>
              </div>
            ) : (
              <div
                className={classNames(
                  "flex flex-col items-center h-80",
                  selectedUsersClassname
                )}
              >
                <h6 className="text-center font-variant-small-caps pd-b-sm">
                  <FormattedMessage
                    defaultMessage="Selected users"
                    id="ekKzYD"
                  />
                </h6>
                <ChatUsersRenderer
                  wrapperClassname={classNames(
                    "h-85 w-100 mg-0-auto pd-xs",
                    isDesktop ? "w-70" : "w-100"
                  )}
                  users={
                    !shouldSubmit && outsideSelectedUsers
                      ? outsideSelectedUsers
                      : selectedUsers ?? []
                  }
                  onClick={removeUser}
                  useDisplayName={false}
                  isLoading={isLoading}
                  isActionLoading={isCreateConversationLoading}
                  actionItemId={conversationToCreate}
                  icon={
                    <Icon
                      icon={["far", "user-plus"]}
                      size="lg"
                      className="pd-r-sm pointer"
                    />
                  }
                  secondIconCondition={{
                    icon: (
                      <Icon
                        icon={["far", "user-minus"]}
                        size="lg"
                        className="pd-r-sm pointer"
                      />
                    ),
                    arrayToFind:
                      !shouldSubmit && outsideSelectedUsers
                        ? outsideSelectedUsers
                        : selectedUsers,
                  }}
                />
              </div>
            )}
            {selectedUsers?.length === 1 && !isPremium && isDisabled && (
              <div
                className="as-strong text-center text-muted pointer flex flex-col justify-center items-center bg-light pd-sm borderR-05 w-90 mg-0-auto gap-md"
                onClick={handlePremiumClick}
              >
                <FormattedMessage
                  defaultMessage="Only premium users can select multiple users and create group chats"
                  id="XjyaWm"
                />
                <small className="font-variant-md-caps">
                  <FormattedMessage
                    defaultMessage="Click here to get premium"
                    id="Un4iyQ"
                  />
                </small>
              </div>
            )}
            {!isEmpty(selectedUsers) && shouldSubmit && (
              <div className="h-10 h-min-5 flex items-end justify-center mg-b-lg">
                <Button
                  variant="success"
                  size="md"
                  className="flex gap-sm items-center font-variant-small-caps"
                  onClick={handleCreateConversation}
                >
                  <FormattedMessage defaultMessage="Create chat" id="8cTD8S" />
                  {isCreateConversationLoading ? (
                    <LoadingIcon size="lg" />
                  ) : (
                    <Icon icon={["far", "paper-plane"]} size="lg" />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </Boundary>
    </div>
  );
};
